import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, LayoutTemplate, Trash2, Loader2, Save, Pencil } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { EmptyState } from "@/components/EmptyState";
import { ListEditor } from "@/components/ListEditor";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const EMPTY = { name: "", cadence: "monthly", include_tldr: true, include_highlights: true, include_lowlights: true, include_asks: true, default_asks: [], body_skeleton: "" };

export default function Templates() {
  const { company } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!company) return;
    setLoading(true);
    try { const { data } = await api.get(`/companies/${company.id}/templates`); setTemplates(data); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [company]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...EMPTY, ...t }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Template needs a name");
    setSaving(true);
    try {
      if (editing) await api.put(`/templates/${editing.id}`, form);
      else await api.post(`/companies/${company.id}/templates`, form);
      toast.success("Template saved");
      setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => { try { await api.delete(`/templates/${id}`); toast.success("Template deleted"); load(); } catch { toast.error("Delete failed"); } };

  const Row = ({ label, k }) => (
    <div className="flex items-center justify-between rounded-lg border px-3 py-2">
      <span className="text-sm">{label}</span>
      <Switch checked={form[k]} onCheckedChange={(v) => set(k, v)} data-testid={`tpl-${k}`} />
    </div>
  );

  return (
    <AppShell title="Templates" subtitle="Reusable update structures" actions={<Button onClick={openNew} className="gap-2" data-testid="new-template-button"><Plus className="h-4 w-4" /> New template</Button>}>
      {loading ? <p className="text-sm text-muted-foreground">Loading...</p> : templates.length === 0 ? (
        <EmptyState icon={LayoutTemplate} title="No templates yet" description="Create a reusable template so every monthly or weekly update starts half-written." actionLabel="Create template" onAction={openNew} />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map((t) => (
            <Card key={t.id} className="rounded-xl border bg-card p-5" data-testid={`template-item-${t.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><LayoutTemplate className="h-5 w-5" /></div>
                <Badge variant="secondary" className="font-normal capitalize">{t.cadence}</Badge>
              </div>
              <div className="mt-3 h-display text-base font-semibold text-foreground">{t.name}</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {t.include_tldr && <Badge variant="outline" className="font-normal">TL;DR</Badge>}
                {t.include_highlights && <Badge variant="outline" className="font-normal">Highlights</Badge>}
                {t.include_lowlights && <Badge variant="outline" className="font-normal">Lowlights</Badge>}
                {t.include_asks && <Badge variant="outline" className="font-normal">Asks</Badge>}
              </div>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" size="sm" className="flex-1 gap-1.5" onClick={() => openEdit(t)} data-testid={`edit-template-${t.id}`}><Pencil className="h-3.5 w-3.5" /> Edit</Button>
                <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-red-600" onClick={() => remove(t.id)} data-testid={`delete-template-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit template" : "New template"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1" placeholder="Monthly Investor Update" data-testid="tpl-name" /></div>
            <div><Label>Cadence</Label>
              <Select value={form.cadence} onValueChange={(v) => set("cadence", v)}>
                <SelectTrigger className="mt-1" data-testid="tpl-cadence"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="quarterly">Quarterly</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Sections to include</Label>
              <Row label="TL;DR" k="include_tldr" />
              <Row label="Highlights" k="include_highlights" />
              <Row label="Lowlights & Challenges" k="include_lowlights" />
              <Row label="Asks" k="include_asks" />
            </div>
            <ListEditor label="Default asks (prefilled)" items={form.default_asks} onChange={(v) => set("default_asks", v)} testId="tpl-asks" />
            <div><Label>Body skeleton (optional Markdown)</Label><Textarea value={form.body_skeleton} onChange={(e) => set("body_skeleton", e.target.value)} className="mt-1 min-h-[120px] font-mono text-xs" placeholder="## Key Metrics\n\n## Highlights\n" data-testid="tpl-body" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="gap-2" data-testid="save-template">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
